import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { Brain, TrendingUp, AlertCircle, Target, Heart, Activity, Zap, Clock, Calendar, BarChart3, Users, Smartphone, Moon, Sun, Coffee, Utensils, Pill, Dumbbell, Shield, TrendingDown, Minus, Plus } from 'lucide-react';
import ComingSoonBanner from '../common/ComingSoonBanner';

interface CorrelationData {
  x: number;
  y: number;
  date: string;
  category?: string;
}

interface TrendData {
  date: string;
  glucose: number;
  heartRate: number;
  steps: number;
  sleep: number;
  stress: number;
  weight: number;
  bloodPressure: string;
  mood: number;
  energy: number;
}

interface StatisticalInsight {
  id: string;
  title: string;
  description: string;
  confidence: number;
  correlation: number;
  significance: 'high' | 'medium' | 'low';
  actionable: boolean;
  variables: string[];
  impact: 'positive' | 'negative' | 'neutral';
  timeframe: string;
  recommendations?: string[];
}

interface HealthRisk {
  id: string;
  category: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  probability: number;
  timeframe: string;
  description: string;
  mitigation: string[];
  trend: 'improving' | 'stable' | 'worsening';
}

interface AnomalyData {
  id: string;
  date: string;
  metric: string;
  value: number;
  expectedValue: number;
  deviation: number;
  severity: 'low' | 'medium' | 'high';
  description: string;
  potentialCauses: string[];
}

interface PredictiveModel {
  id: string;
  name: string;
  accuracy: number;
  targetMetric: string;
  predictionHorizon: string;
  lastUpdated: string;
  status: 'active' | 'training' | 'inactive';
  features: string[];
}

export const AdvancedAnalytics: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('30-days');
  const [activeAnalysis, setActiveAnalysis] = useState<string>('correlations');
  const [selectedMetric, setSelectedMetric] = useState('glucose');

  const [trendData] = useState<TrendData[]>([
    { date: '2024-01-01', glucose: 142, heartRate: 72, steps: 8500, sleep: 7.2, stress: 6, weight: 185, bloodPressure: '135/85', mood: 7, energy: 6 },
    { date: '2024-01-02', glucose: 138, heartRate: 75, steps: 9200, sleep: 6.8, stress: 7, weight: 184.5, bloodPressure: '138/87', mood: 6, energy: 5 },
    { date: '2024-01-03', glucose: 145, heartRate: 68, steps: 7800, sleep: 8.1, stress: 4, weight: 184, bloodPressure: '132/83', mood: 8, energy: 7 },
    { date: '2024-01-04', glucose: 150, heartRate: 78, steps: 6500, sleep: 6.2, stress: 8, weight: 185, bloodPressure: '145/90', mood: 5, energy: 4 },
    { date: '2024-01-05', glucose: 135, heartRate: 70, steps: 10200, sleep: 7.8, stress: 3, weight: 183.5, bloodPressure: '130/82', mood: 9, energy: 8 },
    { date: '2024-01-06', glucose: 141, heartRate: 73, steps: 8900, sleep: 7.5, stress: 5, weight: 183, bloodPressure: '133/84', mood: 7, energy: 6 },
    { date: '2024-01-07', glucose: 148, heartRate: 76, steps: 7200, sleep: 6.5, stress: 7, weight: 184, bloodPressure: '140/88', mood: 6, energy: 5 },
  ]);

  const [correlationData] = useState<CorrelationData[]>([
    { x: 6.2, y: 150, date: '2024-01-04', category: 'sleep-glucose' },
    { x: 6.5, y: 148, date: '2024-01-07', category: 'sleep-glucose' },
    { x: 6.8, y: 138, date: '2024-01-02', category: 'sleep-glucose' },
    { x: 7.2, y: 142, date: '2024-01-01', category: 'sleep-glucose' },
    { x: 7.5, y: 141, date: '2024-01-06', category: 'sleep-glucose' },
    { x: 7.8, y: 135, date: '2024-01-05', category: 'sleep-glucose' },
    { x: 8.1, y: 145, date: '2024-01-03', category: 'sleep-glucose' },
  ]);

  const [insights] = useState<StatisticalInsight[]>([
    {
      id: '1',
      title: 'Sleep-Glucose Correlation',
      description: 'Poor sleep quality (< 7 hours) shows strong correlation with elevated morning glucose levels',
      confidence: 87,
      correlation: -0.73,
      significance: 'high',
      actionable: true,
      variables: ['Sleep Duration', 'Morning Glucose'],
      impact: 'negative',
      timeframe: 'Next 30 days',
      recommendations: ['Aim for 7-8 hours of sleep', 'Establish consistent bedtime routine', 'Avoid late-night meals']
    },
    {
      id: '2',
      title: 'Exercise Impact Pattern',
      description: 'Daily steps above 8,000 correlate with 12% lower average glucose levels over 7-day periods',
      confidence: 82,
      correlation: -0.68,
      significance: 'high',
      actionable: true,
      variables: ['Daily Steps', 'Average Glucose'],
      impact: 'positive',
      timeframe: 'Next 2 weeks',
      recommendations: ['Set daily step goal of 8,000+', 'Take walking breaks during work', 'Use stairs instead of elevator']
    },
    {
      id: '3',
      title: 'Stress-Heart Rate Variability',
      description: 'Self-reported stress levels show moderate correlation with heart rate variability patterns',
      confidence: 74,
      correlation: 0.61,
      significance: 'medium',
      actionable: true,
      variables: ['Stress Level', 'Heart Rate Variability'],
      impact: 'negative',
      timeframe: 'Next 7 days',
      recommendations: ['Practice daily meditation', 'Take stress management breaks', 'Monitor stress triggers']
    },
    {
      id: '4',
      title: 'Medication Timing Effect',
      description: 'Taking medication within 2 hours of meals shows 15% better glucose control outcomes',
      confidence: 91,
      correlation: -0.79,
      significance: 'high',
      actionable: true,
      variables: ['Medication Timing', 'Post-meal Glucose'],
      impact: 'positive',
      timeframe: 'Next 30 days',
      recommendations: ['Set medication reminders', 'Coordinate with meal timing', 'Track medication adherence']
    }
  ]);

  const [healthRisks] = useState<HealthRisk[]>([
    {
      id: '1',
      category: 'Cardiovascular Health',
      riskLevel: 'medium',
      probability: 65,
      timeframe: 'Next 6 months',
      description: 'Elevated blood pressure readings trending upward, combined with stress patterns',
      mitigation: ['Increase physical activity', 'Reduce sodium intake', 'Stress management techniques'],
      trend: 'worsening'
    },
    {
      id: '2',
      category: 'Diabetes Management',
      riskLevel: 'low',
      probability: 25,
      timeframe: 'Next 3 months',
      description: 'Glucose levels showing improvement with current management strategies',
      mitigation: ['Continue current exercise routine', 'Maintain dietary changes', 'Regular monitoring'],
      trend: 'improving'
    },
    {
      id: '3',
      category: 'Sleep Quality',
      riskLevel: 'high',
      probability: 80,
      timeframe: 'Next 30 days',
      description: 'Consistent sleep deprivation affecting multiple health metrics',
      mitigation: ['Establish bedtime routine', 'Limit screen time before bed', 'Create sleep-friendly environment'],
      trend: 'worsening'
    }
  ]);

  const [anomalies] = useState<AnomalyData[]>([
    {
      id: '1',
      date: '2024-01-04',
      metric: 'Glucose',
      value: 150,
      expectedValue: 135,
      deviation: 11.1,
      severity: 'high',
      description: 'Unexpected glucose spike following poor sleep night',
      potentialCauses: ['Sleep deprivation', 'Stress increase', 'Dietary changes']
    },
    {
      id: '2',
      date: '2024-01-07',
      metric: 'Blood Pressure',
      value: 140,
      expectedValue: 130,
      deviation: 7.7,
      severity: 'medium',
      description: 'Elevated blood pressure reading outside normal range',
      potentialCauses: ['Stress', 'Sodium intake', 'Lack of exercise']
    }
  ]);

  const [predictiveModels] = useState<PredictiveModel[]>([
    {
      id: '1',
      name: 'Glucose Prediction Model',
      accuracy: 89,
      targetMetric: 'Glucose Levels',
      predictionHorizon: '24 hours',
      lastUpdated: '2024-01-15',
      status: 'active',
      features: ['Sleep duration', 'Exercise level', 'Stress level', 'Dietary patterns']
    },
    {
      id: '2',
      name: 'Blood Pressure Trend Model',
      accuracy: 76,
      targetMetric: 'Blood Pressure',
      predictionHorizon: '7 days',
      lastUpdated: '2024-01-14',
      status: 'active',
      features: ['Stress patterns', 'Exercise frequency', 'Sleep quality', 'Dietary sodium']
    },
    {
      id: '3',
      name: 'Weight Management Model',
      accuracy: 82,
      targetMetric: 'Weight',
      predictionHorizon: '30 days',
      lastUpdated: '2024-01-13',
      status: 'active',
      features: ['Calorie intake', 'Exercise level', 'Sleep patterns', 'Stress levels']
    }
  ]);

  const getSignificanceColor = (significance: string) => {
    switch (significance) {
      case 'high': return 'text-green-700 bg-green-100 border-green-300';
      case 'medium': return 'text-yellow-700 bg-yellow-100 border-yellow-300';
      case 'low': return 'text-gray-700 bg-gray-100 border-gray-300';
      default: return 'text-gray-700 bg-gray-100 border-gray-300';
    }
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'critical': return 'text-red-700 bg-red-100 border-red-300';
      case 'high': return 'text-orange-700 bg-orange-100 border-orange-300';
      case 'medium': return 'text-yellow-700 bg-yellow-100 border-yellow-300';
      case 'low': return 'text-green-700 bg-green-100 border-green-300';
      default: return 'text-gray-700 bg-gray-100 border-gray-300';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'worsening': return <TrendingDown className="h-4 w-4 text-red-600" />;
      case 'stable': return <Minus className="h-4 w-4 text-gray-600" />;
      default: return <Minus className="h-4 w-4 text-gray-600" />;
    }
  };

  const analysisTypes = [
    { id: 'correlations', label: 'Correlations', icon: TrendingUp },
    { id: 'patterns', label: 'Pattern Analysis', icon: Brain },
    { id: 'predictions', label: 'Predictive Models', icon: Target },
    { id: 'anomalies', label: 'Anomaly Detection', icon: AlertCircle },
    { id: 'risks', label: 'Risk Assessment', icon: Shield },
  ];

  const metrics = [
    { id: 'glucose', label: 'Glucose', icon: Activity },
    { id: 'heartRate', label: 'Heart Rate', icon: Heart },
    { id: 'steps', label: 'Steps', icon: Zap },
    { id: 'sleep', label: 'Sleep', icon: Moon },
    { id: 'stress', label: 'Stress', icon: Brain },
    { id: 'weight', label: 'Weight', icon: Target },
    { id: 'mood', label: 'Mood', icon: Sun },
    { id: 'energy', label: 'Energy', icon: Zap },
  ];

  return (
    <div className="space-y-6">
      {/* Coming Soon Banner */}
      <ComingSoonBanner 
        featureName="Advanced Health Analytics"
        description="Your analytics dashboard will provide real-time statistical analysis, machine learning insights, predictive modeling, and personalized health trend analysis based on your actual health data."
        currentPage="/analytics"
        variant="compact"
        expectedRelease="very soon"
        showPreview={true}
      />
      
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">Advanced Analytics</h2>
          <p className="text-slate-600 mt-1">AI-powered statistical analysis of your health data</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="7-days">Last 7 days</option>
            <option value="30-days">Last 30 days</option>
            <option value="90-days">Last 90 days</option>
            <option value="1-year">Last year</option>
          </select>
          <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
            <Brain className="h-4 w-4" />
            <span>Generate Analysis</span>
          </button>
        </div>
      </div>

      {/* Analysis Type Selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex space-x-4 overflow-x-auto">
          {analysisTypes.map((type) => {
            const Icon = type.icon;
            const isActive = activeAnalysis === type.id;
            return (
              <button
                key={type.id}
                onClick={() => setActiveAnalysis(type.id)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-colors whitespace-nowrap ${
                  isActive
                    ? 'bg-purple-50 text-purple-700 border border-purple-200'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className={`h-5 w-5 ${isActive ? 'text-purple-500' : 'text-slate-400'}`} />
                <span className="font-medium">{type.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Metric Selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Select Metrics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {metrics.map((metric) => {
            const Icon = metric.icon;
            const isActive = selectedMetric === metric.id;
            return (
              <button
                key={metric.id}
                onClick={() => setSelectedMetric(metric.id)}
                className={`flex items-center space-x-2 p-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-slate-600 hover:bg-slate-50 border border-transparent'
                }`}
              >
                <Icon className={`h-4 w-4 ${isActive ? 'text-blue-500' : 'text-slate-400'}`} />
                <span className="text-sm font-medium">{metric.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content based on active analysis */}
      {activeAnalysis === 'correlations' && (
        <div className="space-y-6">
          {/* Statistical Insights */}
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center space-x-3">
                <Brain className="h-6 w-6 text-purple-600" />
                <h3 className="text-lg font-semibold text-slate-800">AI-Generated Insights</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {insights.map((insight) => (
                  <div key={insight.id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="font-semibold text-slate-800">{insight.title}</h4>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getSignificanceColor(insight.significance)}`}>
                          {insight.significance} significance
                        </span>
                        <span className="text-sm text-slate-600">{insight.confidence}%</span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-slate-600 mb-3">{insight.description}</p>
                    
                    <div className="flex flex-wrap gap-1 mb-3">
                      {insight.variables.map((variable, index) => (
                        <span key={index} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                          {variable}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between text-sm text-slate-500 mb-3">
                      <span>Correlation: {insight.correlation.toFixed(2)}</span>
                      <span>{insight.timeframe}</span>
                    </div>

                    {insight.recommendations && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <p className="text-xs text-slate-500 mb-2">Recommendations:</p>
                        <ul className="space-y-1">
                          {insight.recommendations.map((rec, index) => (
                            <li key={index} className="text-xs text-slate-600 flex items-start space-x-2">
                              <span className="text-blue-500 mt-1">•</span>
                              <span>{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Correlation Chart */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Sleep vs Glucose Correlation</h3>
            <ResponsiveContainer width="100%" height={300}>
              <ScatterChart data={correlationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="x" name="Sleep Hours" />
                <YAxis dataKey="y" name="Glucose (mg/dL)" />
                <Tooltip />
                <Scatter dataKey="y" fill="#8884d8" />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {activeAnalysis === 'risks' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center space-x-3">
                <Shield className="h-6 w-6 text-red-600" />
                <h3 className="text-lg font-semibold text-slate-800">Health Risk Assessment</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="grid gap-6">
                {healthRisks.map((risk) => (
                  <div key={risk.id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <h4 className="font-semibold text-slate-800">{risk.category}</h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getRiskColor(risk.riskLevel)}`}>
                          {risk.riskLevel} risk
                        </span>
                        {getTrendIcon(risk.trend)}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-slate-800">{risk.probability}%</p>
                        <p className="text-xs text-slate-500">probability</p>
                      </div>
                    </div>
                    
                    <p className="text-sm text-slate-600 mb-3">{risk.description}</p>
                    
                    <div className="flex items-center justify-between text-sm text-slate-500 mb-3">
                      <span>Timeframe: {risk.timeframe}</span>
                      <span className="capitalize">{risk.trend}</span>
                    </div>

                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs text-slate-500 mb-2">Mitigation strategies:</p>
                      <ul className="space-y-1">
                        {risk.mitigation.map((strategy, index) => (
                          <li key={index} className="text-xs text-slate-600 flex items-start space-x-2">
                            <span className="text-green-500 mt-1">•</span>
                            <span>{strategy}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeAnalysis === 'anomalies' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center space-x-3">
                <AlertCircle className="h-6 w-6 text-orange-600" />
                <h3 className="text-lg font-semibold text-slate-800">Anomaly Detection</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="grid gap-4">
                {anomalies.map((anomaly) => (
                  <div key={anomaly.id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <h4 className="font-semibold text-slate-800">{anomaly.metric}</h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          anomaly.severity === 'high' ? 'bg-red-100 text-red-700' :
                          anomaly.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {anomaly.severity} severity
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-800">{anomaly.value}</p>
                        <p className="text-xs text-slate-500">vs {anomaly.expectedValue} expected</p>
                      </div>
                    </div>
                    
                    <p className="text-sm text-slate-600 mb-3">{anomaly.description}</p>
                    
                    <div className="flex items-center justify-between text-sm text-slate-500 mb-3">
                      <span>Deviation: {anomaly.deviation}%</span>
                      <span>{anomaly.date}</span>
                    </div>

                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs text-slate-500 mb-2">Potential causes:</p>
                      <div className="flex flex-wrap gap-1">
                        {anomaly.potentialCauses.map((cause, index) => (
                          <span key={index} className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">
                            {cause}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeAnalysis === 'predictions' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center space-x-3">
                <Target className="h-6 w-6 text-green-600" />
                <h3 className="text-lg font-semibold text-slate-800">Predictive Models</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="grid gap-4">
                {predictiveModels.map((model) => (
                  <div key={model.id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-slate-800">{model.name}</h4>
                        <p className="text-sm text-slate-600">Target: {model.targetMetric}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-slate-800">{model.accuracy}%</p>
                        <p className="text-xs text-slate-500">accuracy</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-slate-500 mb-3">
                      <span>Horizon: {model.predictionHorizon}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        model.status === 'active' ? 'bg-green-100 text-green-700' :
                        model.status === 'training' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {model.status}
                      </span>
                    </div>

                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs text-slate-500 mb-2">Features used:</p>
                      <div className="flex flex-wrap gap-1">
                        {model.features.map((feature, index) => (
                          <span key={index} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trend Chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Health Trends Over Time</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="glucose" stroke="#8884d8" strokeWidth={2} />
            <Line type="monotone" dataKey="heartRate" stroke="#82ca9d" strokeWidth={2} />
            <Line type="monotone" dataKey="stress" stroke="#ffc658" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
